import { createActionGroup, createFeature, createReducer, createSelector, on, props } from '@ngrx/store';

import { isNil } from 'lodash';

import { environment } from '../../environments/environment';
import { BaseCommentModeFragment, ZoneBranding , FullPermissionFragment, ZoneConfigValue, CurrentBrandingQuery, BaseExpenseFragment, QuillTemplate, BaseTaxFragment, AvailableZonesAndCurrentZoneQuery } from '../../generated/graphql.generated';
import { EventTypeInfo, defaultCountry, defaultEventTypeInfoMapV2, jurisdictions } from '../global.constants';
import { filterOutProductsWithInactivePrices, sortProductsByCategory } from '../job/job-charges-helpers';
import { WatchPermissionsAndRestrictionsInput } from '../services/permission.service';
import { permissionHasRestrictions, resolvePermissionFromArray } from '../services/permissions.util';
import { brandingTemplatesReducers } from '../templates/state/branding-templates.reducer';

const checkPermissions = (allPermissions: FullPermissionFragment[], permissionsAndRestrictions: WatchPermissionsAndRestrictionsInput[]) => {

	const mappedResults = permissionsAndRestrictions.map((input) => {
		const permission = resolvePermissionFromArray(allPermissions, input.permission);
		if (!permission) { return undefined; }
		if (!input.restriction) { return permission; }

		if (!permissionHasRestrictions(permission, input.restriction)) {
			return undefined;
		}

		return permission;
	});

	return mappedResults.map(Boolean);
};


export const BrandingActions = createActionGroup({
	source: 'Branding',
	events: {
		'Branding Loading': props<{
			loading: boolean;
		}>(),
		'Branding Load Error': props<{
			error: Error;
		}>(),
		'Branding Updated': props<{
			branding: ZoneBranding;
			features: string[];
			commentModes: BaseCommentModeFragment[];
			// The QUERY's shape, not the schema type. They differ wherever the
			// branding query selects a subset of a config's fields — `PaymentType`
			// declares a non-null `id` that a payment-type config value
			// deliberately does not carry, so the query asks only for `enabled`.
			// This is also what the reducer already stores.
			configValues: CurrentBrandingQuery['getZoneConfigValues'];
			permissions: FullPermissionFragment[];
			products: CurrentBrandingQuery['products']['products'];
			taxes: BaseTaxFragment[];
			expenses: BaseExpenseFragment[];
            templates: QuillTemplate[],
		}>(),
		'Available Zones Loaded': props<{
			availableZones: AvailableZonesAndCurrentZoneQuery['availableZones'];
		}>(),
	},
});

const brandingInitialState = {
	branding: undefined as ZoneBranding,
	configs: {} as CurrentBrandingQuery['getZoneConfigValues'],
	commentModes: [] as BaseCommentModeFragment[],
	country: defaultCountry,
	permissions: [] as FullPermissionFragment[],

	currency: defaultCountry.currency,
	units: defaultCountry.units,

	loading: true as boolean,
	products: [] as CurrentBrandingQuery['products']['products'],
	taxes: [] as BaseTaxFragment[],
	expenses: [] as BaseExpenseFragment[],
    templates: [] as QuillTemplate[],
	availableZones: [] as AvailableZonesAndCurrentZoneQuery['availableZones'],
};

export type BrandingState = typeof brandingInitialState;

export const brandingFeature = createFeature({
	name: 'Branding',
	reducer: createReducer(
		brandingInitialState,
		on(BrandingActions.brandingUpdated, (state, {
			branding, configValues, permissions,
			products, commentModes, taxes, expenses, templates,
		}): BrandingState => {
			const configs = configValues;

			// configValues may be null if we are not authenticated
			// for (const config of configValues || []) {
			// 	configs[config.key] = formatConfigValue(config);
			// }

			const countryCode = branding.country || environment.defaultCountry;

			let country = jurisdictions.find((j) => j.country === countryCode);
			if (!country) {
				country = jurisdictions.find((j) => j.country === environment.defaultCountry);
			}

			const currency = country.currency;
			const units = country.units;

			const productsWithActivePrices = filterOutProductsWithInactivePrices(products);
			const productsSortedByCategory = sortProductsByCategory(productsWithActivePrices);

			return {
				...state,
				commentModes,
				branding,
				configs,
				currency,
				permissions,
				units,
				products: productsSortedByCategory,
				loading: false,
				taxes,
				expenses,
				templates,
			};
		}),
		on(BrandingActions.brandingLoading, (state, { loading }): BrandingState => {
			return {
				...state,
				loading,
			}
		}),
		on(BrandingActions.availableZonesLoaded, (state, { availableZones }): BrandingState => {
			return {
				...state,
				availableZones,
			}
		}),
        ...brandingTemplatesReducers,
	),
});

export function createConfigSelector<T extends keyof CurrentBrandingQuery['getZoneConfigValues']>(
  configKey: T,
  defaultValue?: ZoneConfigValue[T],
) {
	return createSelector(brandingFeature.selectConfigs, (configs) => {
		return configs[configKey] as ZoneConfigValue[T] || defaultValue;
	});
}

export function createCheckMuliplePermissionsSelector(permissionsAndRestrictions: WatchPermissionsAndRestrictionsInput[]) {
	// Create a new selector that will return a boolean array
	return createSelector(brandingFeature.selectPermissions,
		(allPermissions) => checkPermissions(allPermissions, permissionsAndRestrictions));
}

export function createCheckSinglePermissionSelector(permissionAndRestriction: WatchPermissionsAndRestrictionsInput) {
	return createSelector(brandingFeature.selectPermissions, (allPermissions) => {
		const [result] = checkPermissions(allPermissions, [permissionAndRestriction]);
		return result;
	});
}

export const selectSystemDetails = createSelector(brandingFeature.selectBranding, (branding) => {
	if (!branding) { return; }
	return {
		timezone: branding.timezone,
		country: branding.country,
		language: branding.language,
	}
});

export const selectQubeSheetsEnabled = createConfigSelector('QUBE__SHEETS__INTEGRATION_ENABLED', false);

export const selectEventTypes = createSelector(
  createConfigSelector('CALENDAREVENTS_TYPES'),
  (eventTypes) => {
    if (isNil(eventTypes)) {
      return defaultEventTypeInfoMapV2;
    }
    return eventTypes as { [key: string ]: EventTypeInfo };
  }
);

/**
 * Navigator script URLs from the per-event-type config keys, keyed by event type.
 *
 * One key per event type defined in code (`defaultEventTypeInfoMapV2`). Event types that
 * exist only in zone config — `book-off`, `otr-fees`, `storage`, `generic` — cannot have a
 * compile-time config key, so they are served by `calendarEvents.types[<type>].navigatorUrl`
 * instead. Resolution order lives in `resolveNavigatorUrl` (`job/job-helpers.ts`).
 *
 * Listed explicitly rather than derived from the event type at runtime: `createConfigSelector`
 * needs a literal key to stay typed, and each key must also be requested by name in
 * `branding.graphql` to reach the store at all.
 */
export const selectNavigatorUrlsByEventType = createSelector(
  createConfigSelector('NAVIGATOR_URL__MOVING'),
  createConfigSelector('NAVIGATOR_URL__ESTIMATING'),
  createConfigSelector('NAVIGATOR_URL__VIRTUALESTIMATE'),
  createConfigSelector('NAVIGATOR_URL__DELIVERY'),
  createConfigSelector('NAVIGATOR_URL__PACKING'),
  createConfigSelector('NAVIGATOR_URL__UNPACKING'),
  createConfigSelector('NAVIGATOR_URL__DRIVING'),
  createConfigSelector('NAVIGATOR_URL__LOAD'),
  createConfigSelector('NAVIGATOR_URL__UNLOAD'),
  (moving, estimating, virtualEstimate, delivery, packing, unpacking, driving, load, unload) => ({
    moving, estimating, virtualEstimate, delivery, packing, unpacking, driving, load, unload,
  } as { [eventType: string]: string | undefined }),
);

/**
 * Selector to get the distance unit preference ('metric' or 'imperial') based on system country.
 * Used by dispatch reports, location effects, and other distance-related features.
 */
export const selectDistanceUnit = createSelector(selectSystemDetails, (systemDetails): 'metric' | 'imperial' => {
	if (!systemDetails) {
		return environment.defaultUnits as 'metric' | 'imperial';
	}
	const country = jurisdictions.find((j) => j.country === systemDetails.country);
	return country?.units || environment.defaultUnits as 'metric' | 'imperial';
});
